import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Icon, IconButton, Tabs } from '../ds';
import { EmptyState, Eyebrow, Mono, MutedHeading, Screen, ScreenTitle } from '../components/primitives';
import { EditSheet } from '../components/EditSheet';
import { Sheet, SheetRow } from '../components/Sheet';
import { DateField, TimeField } from '../components/TimeField';
import { useToast } from '../components/Toast';
import * as api from '../lib/db';
import { useFamily } from '../state/FamilyContext';
import { entryAmountLabel, entryTitle, entryTone, entryTypeIcon } from '../lib/labels';
import { TAB_TYPES, entryTouchesDay, isOpenSleep, resolveSleepEnd, totalSleepMinutes } from '../lib/entries';
import {
  addDays,
  dayContextLabel,
  dayKey,
  formatDateShortJa,
  formatDurationCompact,
  formatDurationJa,
  formatTime,
  minutesBetween,
  parseISODate,
} from '../lib/time';
import type { Entry, SleepEntry } from '../lib/types';

/**
 * きろく / Log.
 *
 * Three tabs over a day's entries. Each row is stamped with the parent who
 * logged it — the same "who did what" thread as the home feed, at detail.
 *
 * A nap with no end shows as 記録中 with a way to close it, because that is
 * the normal state of a nap you fell asleep during.
 *
 * The banner's arrows step back through history: today and yesterday come
 * from the context's live window, and any older day opens its own
 * subscription — the same pattern as the prep screen's day walking.
 */

type LogTab = 'feed' | 'diaper' | 'sleep';

const TABS: { value: LogTab; label: string }[] = [
  { value: 'feed', label: '授乳' },
  { value: 'diaper', label: 'おむつ' },
  { value: 'sleep', label: 'ねんね' },
];

export function LogScreen() {
  const { familyId, family, members, me, children, entries, today } = useFamily();
  const toast = useToast();

  const [tab, setTab] = useState<LogTab>('feed');
  const [closing, setClosing] = useState<SleepEntry | null>(null);
  const [editing, setEditing] = useState<Entry | null>(null);

  /** Days back from today. 0 follows the context's midnight rollover. */
  const [dayOffset, setDayOffset] = useState(0);
  const selectedDate = useMemo(() => addDays(parseISODate(today), dayOffset), [today, dayOffset]);
  const selectedKey = dayKey(selectedDate);

  /**
   * Days the context's live entries already cover: the subscription reaches
   * the start of yesterday, and anything older present in the list (a window
   * left open across midnight, or the preview's fixtures) is live too. Older
   * days open their own subscription below.
   */
  const liveFloorKey = useMemo(() => {
    let floor = dayKey(addDays(parseISODate(today), -1));
    for (const e of entries) {
      const key = dayKey(e.at);
      if (key < floor) floor = key;
    }
    return floor;
  }, [entries, today]);
  const isLiveDay = selectedKey >= liveFloorKey;

  const [dayEntries, setDayEntries] = useState<Entry[] | null>(null);
  useEffect(() => {
    if (isLiveDay || !familyId) {
      setDayEntries(null);
      return;
    }
    setDayEntries(null);
    return api.watchEntriesForDay(familyId, parseISODate(selectedKey), setDayEntries, () =>
      setDayEntries([]),
    );
  }, [familyId, isLiveDay, selectedKey]);

  const sourceEntries = isLiveDay ? entries : (dayEntries ?? []);
  const loadingDay = !isLiveDay && dayEntries === null;

  const babies = useMemo(() => children.filter((c) => c.kind === 'baby'), [children]);
  const [childId, setChildId] = useState<string>('');
  const baby = babies.find((c) => c.id === childId) ?? babies[0] ?? children[0] ?? null;

  const memberByUid = useMemo(() => new Map(members.map((m) => [m.uid, m])), [members]);
  const now = new Date();

  const rows = useMemo(() => {
    const types = TAB_TYPES[tab];
    return sourceEntries
      .filter((e) => types.includes(e.type))
      .filter((e) => !baby || e.childId === baby.id)
      .filter((e) => entryTouchesDay(e, selectedDate))
      .sort((a, b) => b.at.getTime() - a.at.getTime());
    // Keyed on the day, not on `now` — see the note in HomeScreen.
  }, [sourceEntries, tab, baby?.id, selectedKey]);

  const dayContext = dayContextLabel(selectedDate, parseISODate(today));

  const summary = useMemo(() => buildSummary(tab, rows, now), [tab, rows]);

  return (
    <Screen>
      <ScreenTitle eyebrow={`Baby · ${baby?.name ?? ''}`} title="記録" />

      {babies.length > 1 ? (
        <div style={{ marginBottom: 12 }}>
          <Tabs
            variant="pill"
            value={baby?.id ?? ''}
            onChange={setChildId}
            items={babies.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <Tabs variant="pill" value={tab} onChange={(v) => setTab(v as LogTab)} items={TABS} />
      </div>

      {/* ---- day stepper — mirrors the prep screen's banner ---- */}
      <Card
        padding="sm"
        style={{
          marginBottom: 16,
          background: dayOffset === 0 ? 'var(--brand-subtle)' : 'var(--surface-sunken)',
          borderColor: 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconButton
            icon="ChevronLeft"
            label="前の日"
            variant="ghost"
            size="sm"
            onClick={() => setDayOffset((o) => o - 1)}
          />
          <button
            type="button"
            onClick={() => setDayOffset(0)}
            disabled={dayOffset === 0}
            aria-label="きょうにもどる"
            title="きょうにもどる"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              background: 'transparent',
              padding: '2px 4px',
              borderRadius: 'var(--radius-md)',
              cursor: dayOffset === 0 ? 'default' : 'pointer',
              color: 'var(--text)',
              font: 'inherit',
              fontSize: 'var(--text-sm)',
              textAlign: 'center',
            }}
          >
            {formatDateShortJa(selectedDate)}の記録
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{dayContext}</div>
          </button>
          <IconButton
            icon="ChevronRight"
            label="次の日"
            variant="ghost"
            size="sm"
            onClick={() => setDayOffset((o) => o + 1)}
            disabled={dayOffset === 0}
          />
        </div>
      </Card>

      {/* ---- daily summary ---- */}
      <Card padding="md" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
          <div style={{ minWidth: 0 }}>
            <Eyebrow>{dayContext}</Eyebrow>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
              {summary.text}
            </div>
          </div>
          <Mono style={{ fontSize: 'var(--text-2xl)', color: 'var(--brand)', lineHeight: 1, flex: 'none' }}>
            {summary.count}
          </Mono>
        </div>
      </Card>

      <MutedHeading>タイムライン · Timeline</MutedHeading>
      <Card padding="none" style={{ overflow: 'hidden' }}>
        {loadingDay ? (
          <div
            style={{
              padding: '30px 16px',
              textAlign: 'center',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-subtle)',
            }}
          >
            よみこみ中…
          </div>
        ) : rows.length ? (
          rows.map((entry, i) => {
            const tone = entryTone(entry.type);
            const author = memberByUid.get(entry.byUid);
            const open = isOpenSleep(entry);
            return (
              <div
                key={entry.id}
                role="button"
                tabIndex={0}
                aria-label={`${entryTitle(entry)} を修正`}
                onClick={() => setEditing(entry)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setEditing(entry);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '13px 15px',
                  borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border)',
                  background: open ? 'var(--brand-subtle)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: tone.bg,
                    color: tone.fg,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name={entryTypeIcon(entry.type)} size={19} />
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                    {entryTitle(entry)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                    <Mono>{timeColumn(entry)}</Mono>
                    {author ? ` · ${author.name}` : ''}
                  </div>
                </div>

                {open ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setClosing(entry);
                    }}
                  >
                    おわり
                  </Button>
                ) : (
                  <Mono
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text)',
                      whiteSpace: 'nowrap',
                      flex: 'none',
                    }}
                  >
                    {entryAmountLabel(entry)}
                  </Mono>
                )}
              </div>
            );
          })
        ) : (
          <EmptyState icon={<Icon name={entryTypeIcon(TAB_TYPES[tab][0])} size={22} />}>
            {tab === 'sleep'
              ? 'まだねんねの記録はありません。'
              : tab === 'diaper'
                ? 'まだおむつの記録はありません。'
                : 'まだ授乳の記録はありません。'}
          </EmptyState>
        )}
      </Card>

      {family?.onDuty && me ? (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-subtle)', textAlign: 'center' }}>
          いまの担当は{family.onDuty === 'mom' ? 'ママ' : 'パパ'}。記録はこの担当で保存されます。
        </div>
      ) : null}

      {/* ---- edit or delete an entry ---- */}
      {familyId && me ? (
        <EditSheet entry={editing} familyId={familyId} me={me} onClose={() => setEditing(null)} />
      ) : null}

      {/* ---- close an open nap ---- */}
      <CloseSleepSheet
        entry={closing}
        onClose={() => setClosing(null)}
        onSave={async (endAt) => {
          if (!familyId || !me || !closing) return;
          await api.closeSleep(familyId, closing.id, endAt, me);
          toast.showSaved(`${formatTime(closing.at)}〜${formatTime(endAt)}`);
          setClosing(null);
        }}
      />
    </Screen>
  );
}

/** Sleep shows a range; an unfinished nap shows how long it has been running. */
function timeColumn(entry: Entry): string {
  if (entry.type !== 'sleep') return formatTime(entry.at);
  if (entry.endAt) return `${formatTime(entry.at)}–${formatTime(entry.endAt)}`;
  return `${formatTime(entry.at)}– 記録中`;
}

function buildSummary(tab: LogTab, rows: Entry[], now: Date): { count: string; text: string } {
  if (tab === 'sleep') {
    const sleeps = rows.filter((e): e is SleepEntry => e.type === 'sleep');
    const minutes = totalSleepMinutes(sleeps, now);
    const running = sleeps.some(isOpenSleep);
    return {
      count: formatDurationCompact(minutes),
      text: sleeps.length
        ? `ねんね 合計${formatDurationJa(minutes)}${running ? '（記録中をふくむ）' : ''}`
        : 'ねんね まだ記録なし',
    };
  }

  const last = rows[0];
  if (tab === 'diaper') {
    return {
      count: `${rows.length}回`,
      text: last ? `おむつ ${rows.length}回 · 最後 ${formatTime(last.at)}` : 'おむつ まだ記録なし',
    };
  }
  return {
    count: `${rows.length}回`,
    text: last ? `授乳・離乳食 ${rows.length}回 · 最後 ${formatTime(last.at)}` : '授乳 まだ記録なし',
  };
}

/**
 * "おわり" sheet.
 *
 * Defaults to now, which is right when you tap it as they wake, and stays
 * editable for when you remember an hour later.
 */
function CloseSleepSheet({
  entry,
  onClose,
  onSave,
}: {
  entry: SleepEntry | null;
  onClose: () => void;
  onSave: (endAt: Date) => Promise<void>;
}) {
  const [endAt, setEndAt] = useState<Date>(() => new Date());
  const [saving, setSaving] = useState(false);
  const [lastId, setLastId] = useState<string | null>(null);

  // Reset the clock whenever a different nap is opened.
  if (entry && entry.id !== lastId) {
    setLastId(entry.id);
    setEndAt(new Date());
  }

  if (!entry) return null;

  /**
   * Same rule as the add/edit sheets: an explicit end date is respected, and
   * the same-day midnight convenience rolls the end to the next morning. A
   * result that is still not after the start is invalid (warning, no save).
   */
  const resolved = resolveSleepEnd(entry.at, endAt);
  const duration = resolved ? minutesBetween(entry.at, resolved) : null;

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow="End a nap"
      title="ねんねのおわり"
      footer={
        <Button
          variant="primary"
          fullWidth
          size="lg"
          iconStart="Check"
          disabled={saving || resolved === null}
          onClick={async () => {
            if (!resolved) return;
            setSaving(true);
            try {
              await onSave(resolved);
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? '保存中…' : '保存する · Save'}
        </Button>
      }
    >
      <SheetRow
        first
        label={
          <div>
            <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>はじまり</div>
            <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Start</div>
          </div>
        }
      >
        <Mono style={{ fontSize: 'var(--text-xl)' }}>{formatTime(entry.at)}</Mono>
      </SheetRow>

      <SheetRow
        label={
          <div>
            <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>おわり</div>
            <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>End · 起きた時間</div>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DateField value={endAt} onChange={setEndAt} />
          <TimeField value={endAt} onChange={setEndAt} />
        </div>
      </SheetRow>

      <SheetRow label={<Eyebrow>長さ · Duration</Eyebrow>}>
        {duration !== null ? (
          <Mono style={{ fontSize: 'var(--text-md)' }}>{formatDurationJa(duration)}</Mono>
        ) : (
          <span className="cjk" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
            おわりがはじまりより前です
          </span>
        )}
      </SheetRow>

      <div
        className="cjk"
        style={{
          display: 'flex',
          gap: 9,
          alignItems: 'flex-start',
          padding: '12px 13px',
          margin: '10px 0 4px',
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-sunken)',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-muted)',
        }}
      >
        <span style={{ color: 'var(--accent)', flex: 'none', marginTop: 1 }}>
          <Icon name="Info" size={15} />
        </span>
        あとから気づいても大丈夫。起きた時間に合わせて直せます。
      </div>
    </Sheet>
  );
}
